import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, X, Mail, ShoppingCart, Heart, BookOpen, User, LogOut, Users } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import BookCover from './BookCover';
import { SparklesText } from './ui/sparkles-text';
import { HyperText } from './ui/hyper-text';
import SlotCounter from './SlotCounter';
import { toast } from 'sonner';

import { loginWithGoogle } from '../utils/auth';

import { BACKEND_URL, API } from '../utils/api';
import { authFetch, clearToken } from '../utils/session';
const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";

const Header = ({ onSearchClick, searchText }) => {
  const navigate = useNavigate();
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subEmail, setSubEmail] = useState('');
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAcquisition, setShowAcquisition] = useState(false);
  const [dynamicText, setDynamicText] = useState('Buscar libros');
  const [searchOpen, setSearchOpen] = useState(false);
  const [allBooks, setAllBooks] = useState([]);
  const [allCharacters, setAllCharacters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [activePanel, setActivePanel] = useState('left');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchInputRef = React.useRef(null); // 'left' = filters, 'right' = books

  // Build left panel items list
  const getLeftItems = () => {
    const items = [];
    const cats = [...new Set(allBooks.map(b => b.category).filter(Boolean))];
    cats.forEach(c => items.push({ type: 'category', value: c, count: allBooks.filter(b => b.category === c).length }));
    const chars = allCharacters.filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()));
    chars.forEach(c => items.push({ type: 'character', value: c.name, count: c.count }));
    return items;
  };
  const getRightItems = () => {
    let f = allBooks;
    if (searchQuery) f = f.filter(b => b.title.toLowerCase().includes(searchQuery.toLowerCase()));
    if (selectedFilter?.type === 'category') f = f.filter(b => b.category === selectedFilter.value);
    if (selectedFilter?.type === 'character') f = f.filter(b => (b.characters || []).includes(selectedFilter.value));
    return f;
  };

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    if (window.location.pathname === '/auth/callback') return;
    authFetch(`${API}/auth/me`, {  }).then(r => r.ok ? r.json() : null).then(u => {
      if (u) {
        setUser(u);
        authFetch(`${API}/user/cart`, {  }).then(r => r.ok ? r.json() : []).then(c => setCartCount(c?.length || 0));
        if (!u.acquisition_source) setShowAcquisition(true);
      }
    }).catch(() => {});

    // Listen for cart updates from other components
    const onCartUpdate = (e) => setCartCount(e.detail || 0);
    window.addEventListener('cart-updated', onCartUpdate);
    return () => window.removeEventListener('cart-updated', onCartUpdate);
  }, []);

  // Fetch books + characters for search
  useEffect(() => {
    fetch(`${API}/books`).then(r => r.json()).then(bks => {
      if (bks?.length) setAllBooks(bks.map(b => ({ ...b, id: b.book_id || b.slug, coverUrl: `${R2_URL}/books/${b.imageFolder}/cover.webp` })));
    }).catch(() => {});
    fetch(`${API}/characters`).then(r => r.json()).then(setAllCharacters).catch(() => {});
  }, []);

  // Dynamic rotating placeholder
  const [dynamicLabel, setDynamicLabel] = useState({ count: 0, word: 'libros' });
  useEffect(() => {
    const items = [];
    if (allBooks.length) items.push({ count: allBooks.length, word: 'libros' });
    if (allCharacters.length) items.push({ count: allCharacters.length, word: 'personajes' });
    const cats = [...new Set(allBooks.map(b => b.category).filter(Boolean))];
    if (cats.length) items.push({ count: cats.length, word: 'categorías' });
    if (!items.length) items.push({ count: 0, word: 'libros' });

    let i = 0;
    setDynamicLabel(items[0]);
    if (items.length <= 1) return;
    const interval = setInterval(() => { i = (i + 1) % items.length; setDynamicLabel(items[i]); }, 3000);
    return () => clearInterval(interval);
  }, [allBooks, allCharacters]);

  // ⌘K shortcut
  useEffect(() => {
    const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(o => !o); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail) return;
    try {
      const captchaToken = executeRecaptcha ? await executeRecaptcha('subscribe') : '';
      const r = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subEmail, captcha_token: captchaToken }),
      });
      if (r.ok) {
        toast.success('¡Suscrito exitosamente!', { description: 'Recibirás nuevos libros semanalmente.' });
        setSubEmail('');
        setSubscribeOpen(false);
      } else {
        const data = await r.json();
        toast.error(data.detail || 'Error al suscribirse');
      }
    } catch {
      toast.error('Error de conexión');
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 md:px-6 h-14 max-w-[1400px] mx-auto">
          {/* Left: Brand */}
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => navigate('/')}>
            <SparklesText className="text-base font-bold tracking-tight" sparklesCount={2} colors={{ first: "#D4A574", second: "#E8C547" }}>
              Museo de la Fe
            </SparklesText>
          </div>

          {/* Center: Search trigger */}
          <button
            onClick={() => setSearchOpen(true)}
            className="hidden sm:flex items-center gap-2 px-5 h-10 rounded-xl bg-secondary/70 hover:bg-secondary text-muted-foreground text-sm transition-colors duration-200 min-w-[300px] md:min-w-[450px]"
          >
            <Search className="size-3.5 opacity-50" />
            <span className="flex-1 text-left text-muted-foreground/60 flex items-center gap-1 overflow-hidden">
              Buscar{' '}
              {dynamicLabel.count > 0 && <SlotCounter key={dynamicLabel.count} value={dynamicLabel.count} className="text-muted-foreground/60 text-sm" />}
              {' '}<span key={dynamicLabel.word} className="animate-in fade-in slide-in-from-bottom-1 duration-500">{dynamicLabel.word}</span>
            </span>
            <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-background/80 border border-black/[0.06] text-[10px] font-medium text-muted-foreground/50">
              <span className="text-xs">⌘</span>K
            </kbd>
          </button>

          {/* Right: Store + About + Cart + User */}
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/preguntas-frecuentes')} className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block">
              FAQ
            </button>
            <button onClick={() => navigate('/faq')} className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block">
              About
            </button>

            {user && (
              <button onClick={() => navigate('/carrito')} className="relative p-2 text-muted-foreground hover:text-foreground transition-colors">
                <ShoppingCart className="size-4" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-foreground text-background text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span>
                )}
              </button>
            )}

            {user ? (
              <div className="relative">
                <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-secondary transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="size-7 rounded-full" />
                  ) : (
                    <div className="size-7 rounded-full bg-secondary flex items-center justify-center"><User className="size-3.5 text-muted-foreground" /></div>
                  )}
                </button>
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 bg-background border border-border rounded-xl shadow-lg py-1 min-w-[180px]">
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-xs font-medium truncate">{user.name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <button onClick={() => { navigate('/biblioteca'); setShowUserMenu(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2"><BookOpen className="size-3.5" /> Mi Biblioteca</button>
                      <button onClick={() => { navigate('/favoritos'); setShowUserMenu(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2"><Heart className="size-3.5" /> Mis Favoritos</button>
                      <button onClick={() => { navigate('/carrito'); setShowUserMenu(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2"><ShoppingCart className="size-3.5" /> Mi Carrito</button>
                      {user.role === 'admin' && (
                        <button onClick={() => { navigate('/admin'); setShowUserMenu(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2"><User className="size-3.5" /> Admin</button>
                      )}
                      <div className="border-t border-border mt-1 pt-1">
                        <button onClick={async () => { await authFetch(`${API}/auth/logout`, { method: 'POST' }); clearToken(); setUser(null); setCartCount(0); setShowUserMenu(false); navigate('/'); }} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2 text-red-500"><LogOut className="size-3.5" /> Cerrar sesión</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Desktop: Ingresar button */}
                <button
                  onClick={() => loginWithGoogle(window.location.pathname)}
                  className="hidden sm:flex items-center gap-1.5 px-4 h-9 rounded-full bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
                >
                  Ingresar
                </button>

                {/* Mobile: search + FAQ always visible, + opens dropdown */}
                <div className="flex sm:hidden items-center gap-1.5">
                  <button
                    data-testid="mobile-menu-search"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center size-9 rounded-xl bg-secondary/70"
                  >
                    <Search className="size-4 text-muted-foreground" />
                  </button>
                  <button
                    data-testid="mobile-menu-faq"
                    onClick={() => navigate('/preguntas-frecuentes')}
                    className="flex items-center justify-center h-9 px-3 rounded-xl bg-secondary/70 text-sm text-muted-foreground font-medium"
                  >
                    FAQ
                  </button>
                  <button
                    data-testid="mobile-menu-about"
                    onClick={() => navigate('/faq')}
                    className="flex items-center justify-center h-9 px-3 rounded-xl bg-secondary/70 text-sm text-muted-foreground font-medium"
                  >
                    About
                  </button>
                  <div className="relative">
                    <button
                      data-testid="mobile-menu-toggle"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      className="flex items-center justify-center size-9 rounded-full bg-foreground text-background transition-all duration-300"
                    >
                      <Plus className={`size-4 transition-transform duration-300 ${mobileMenuOpen ? 'rotate-45' : 'rotate-0'}`} />
                    </button>
                    {mobileMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setMobileMenuOpen(false)} />
                        <div className="absolute right-0 top-full mt-2 z-50 bg-background border border-border rounded-2xl shadow-xl p-2 min-w-[160px] animate-in fade-in slide-in-from-top-2 duration-200">
                          <button
                            data-testid="mobile-menu-login"
                            onClick={() => { loginWithGoogle(window.location.pathname); setMobileMenuOpen(false); }}
                            className="w-full flex items-center justify-center gap-1.5 px-4 h-10 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
                          >
                            Ingresar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Subscribe Dialog */}
      <Dialog open={subscribeOpen} onOpenChange={setSubscribeOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Suscríbete a Museo de la Fe</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Recibe nuevos libritos y novedades en tu correo
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubscribe} className="space-y-4 pt-2">
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                placeholder="Tu correo electrónico"
                required
                className="flex-1 h-10 px-3 rounded-xl bg-secondary text-sm outline-none focus:ring-2 focus:ring-foreground/20 transition-shadow placeholder:text-muted-foreground/50"
              />
              <button
                type="submit"
                className="h-10 px-4 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors flex items-center gap-2"
              >
                <Mail className="size-4" />
                Subscribe
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Acquisition survey — first time only */}
      <Dialog open={showAcquisition} onOpenChange={setShowAcquisition}>
        <DialogContent className="sm:max-w-sm !z-[200]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">¡Bienvenido!</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Una pregunta rápida para conocerte mejor</DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium mt-2 mb-3">¿Cómo nos encontraste?</p>
          <div className="space-y-2">
            {[
              { value: 'redes_sociales', label: 'Redes sociales (Instagram, Facebook, TikTok)' },
              { value: 'recomendacion', label: 'Un amigo o familiar me recomendó' },
              { value: 'google', label: 'Búsqueda en Google' },
              { value: 'iglesia', label: 'Iglesia o comunidad' },
              { value: 'otro', label: 'Otro' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={async () => {
                  await authFetch(`${API}/user/acquisition`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ source: opt.value }),
                  });
                  setShowAcquisition(false);
                  toast.success('¡Gracias!');
                }}
                className="w-full text-left px-4 py-3 rounded-xl border border-border text-sm hover:bg-secondary hover:border-foreground/20 transition-colors"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Search dialog */}
      <Dialog open={searchOpen} onOpenChange={(open) => { setSearchOpen(open); if (!open) { setSelectedFilter(null); setSearchQuery(''); } }}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl p-0 gap-0 overflow-hidden !z-[200] !top-[5%] sm:!top-[15%] !translate-y-0 !rounded-2xl shadow-2xl max-h-[90vh] sm:max-h-[80vh]">
          <div className="flex items-center gap-2 px-3 sm:px-4 h-11 sm:h-12 border-b border-border">
            <Search className="size-4 text-muted-foreground/50 shrink-0" />
            <input value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setSelectedIndex(-1); }}
              onKeyDown={(e) => {
                const left = getLeftItems();
                const right = getRightItems();
                const max = activePanel === 'left' ? left.length - 1 : right.length - 1;
                if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, max)); }
                if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, -1)); }
                if (e.key === 'ArrowRight') { e.preventDefault(); setActivePanel('right'); setSelectedIndex(-1); }
                if (e.key === 'ArrowLeft') { e.preventDefault(); setActivePanel('left'); setSelectedIndex(-1); }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (activePanel === 'left' && selectedIndex >= 0 && left[selectedIndex]) {
                    const item = left[selectedIndex];
                    setSelectedFilter(selectedFilter?.value === item.value ? null : { type: item.type, value: item.value });
                    setActivePanel('right'); setSelectedIndex(-1);
                  } else if (activePanel === 'right' && selectedIndex >= 0 && right[selectedIndex]) {
                    setSearchOpen(false); navigate(`/libro/${right[selectedIndex].slug}`);
                  }
                }
              }}
              placeholder={`Buscar ${dynamicLabel.count} ${dynamicLabel.word}...`} className="flex-1 text-sm outline-none bg-transparent placeholder:text-muted-foreground/50" autoFocus ref={searchInputRef} />
            <kbd className="hidden sm:inline text-[10px] text-muted-foreground/40 px-1.5 py-0.5 rounded border border-border">Esc</kbd>
          </div>
          {/* Trending marquee */}
          {allBooks.length > 0 && (
            <div className="border-b border-border py-2 sm:py-3 overflow-hidden">
              <div className="flex items-center gap-2 px-3 sm:px-4 mb-1.5 sm:mb-2">
                <p className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider">Trending</p>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400"></span>
                </span>
              </div>
              <div className="relative overflow-hidden">
                <div className="flex gap-2 animate-marquee hover:[animation-play-state:paused]" style={{ width: 'max-content' }}>
                  {[...allBooks, ...allBooks, ...allBooks].map((b, i) => (
                    <button key={`t-${i}`} onClick={() => { setSearchOpen(false); navigate(`/libro/${b.slug}`); }} className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden hover:scale-110 transition-transform">
                      <img src={`${R2_URL}/books/${b.imageFolder}/page_1.webp`} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row min-h-[240px] sm:min-h-[350px] max-h-[55vh] sm:max-h-[450px]">
            <div className="sm:w-1/2 border-b sm:border-b-0 sm:border-r border-border overflow-y-auto max-h-[30vh] sm:max-h-none">
              {(() => {
                const items = getLeftItems();
                const cats = [...new Set(allBooks.map(b => b.category).filter(Boolean))];
                const chars = allCharacters.filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()));
                let idx = 0;
                return (
                  <>
                    {cats.length > 0 && <div className="px-3 pt-3 pb-1"><p className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider px-1 mb-1">Categorías</p></div>}
                    {cats.map(cat => {
                      const i = idx++;
                      const count = allBooks.filter(b => b.category === cat).length;
                      return (
                        <button key={cat} onClick={() => { setSelectedFilter(selectedFilter?.value === cat ? null : { type: 'category', value: cat }); setActivePanel('right'); setSelectedIndex(-1); setTimeout(() => searchInputRef.current?.focus(), 10); }}
                          className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${activePanel === 'left' && selectedIndex === i ? 'bg-secondary/80' : ''} ${selectedFilter?.value === cat ? 'bg-secondary font-semibold' : 'hover:bg-secondary/50'}`}>
                          <span className="flex items-center gap-2"><BookOpen className="size-3.5 text-muted-foreground/60" />{cat}</span>
                          <span className="text-xs text-muted-foreground">{count}</span>
                        </button>
                      );
                    })}
                    {chars.length > 0 && <div className="px-3 pt-4 pb-1"><p className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider px-1 mb-1">Personajes</p></div>}
                    {chars.map(ch => {
                      const i = idx++;
                      return (
                        <button key={ch.name} onClick={() => { setSelectedFilter(selectedFilter?.value === ch.name ? null : { type: 'character', value: ch.name }); setActivePanel('right'); setSelectedIndex(-1); setTimeout(() => searchInputRef.current?.focus(), 10); }}
                          className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${activePanel === 'left' && selectedIndex === i ? 'bg-secondary/80' : ''} ${selectedFilter?.value === ch.name ? 'bg-secondary font-semibold' : 'hover:bg-secondary/50'}`}>
                          <span className="flex items-center gap-2"><Users className="size-3.5 text-muted-foreground/60" />{ch.name}</span>
                          <span className="text-xs text-muted-foreground">{ch.count}</span>
                        </button>
                      );
                    })}
                  </>
                );
              })()}
            </div>
            <div className="sm:w-1/2 overflow-y-auto p-3 sm:p-4 flex-1">
              {(() => {
                let filtered = allBooks;
                if (searchQuery) filtered = filtered.filter(b => b.title.toLowerCase().includes(searchQuery.toLowerCase()));
                if (selectedFilter?.type === 'category') filtered = filtered.filter(b => b.category === selectedFilter.value);
                if (selectedFilter?.type === 'character') filtered = filtered.filter(b => (b.characters || []).includes(selectedFilter.value));
                if (!filtered.length) return <p className="text-sm text-muted-foreground/50 text-center pt-8">No se encontraron libros</p>;
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {filtered.map((b, idx) => (
                      <button key={b.slug} onClick={() => { setSearchOpen(false); navigate(`/libro/${b.slug}`); }} className={`rounded-lg overflow-hidden hover:scale-105 transition-all duration-200 ${activePanel === 'right' && selectedIndex === idx ? 'ring-2 ring-foreground/30 scale-105' : ''}`}>
                        <div className="aspect-[3/4]">
                          <img src={`${R2_URL}/books/${b.imageFolder}/page_1.webp`} alt={b.title} className="w-full h-full object-contain rounded-lg" />
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-t border-border text-[10px] text-muted-foreground/40">
            <span>museodelafe.com</span>
            <span className="hidden sm:inline">↑↓ Navegar &nbsp; ← → Preview &nbsp; ↵ Seleccionar</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
